import { Badge, Stack, Table, Text, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useCallback, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router'
import { DeploymentDetailsDrawer } from '@/components/deployments/deployment-details/DeploymentDetailsDrawer'
import { getGroupExclusionReasonIds } from '@/components/deployments/deployment-editor/DeployPreview/DeployPlanSection/groupExclusionReasons'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { buildCreateDeploymentLink } from '@/router/paths'
import {
  Deployment,
  DeploymentStatus,
  InputSourceType
} from '@/types/deployment'
import { Device } from '@/types/device'
import { CrudAction, Resource } from '@/types/permissions'
import {
  getDeviceGpus,
  getDevicesCameras,
  isJetsonDevice
} from '@/utils/device/getDeviceAttr'
import {
  getSingleCameraReasons,
  resolveSingleDeploymentGpu
} from '../DeploymentTree/deploymentTreeHelpers'
import { DeploymentDeleteConfirm } from './DeploymentDeleteConfirm'
import { DeploymentErrorSpoiler } from './DeploymentErrorSpoiler'
import { DeploymentInputSource } from './DeploymentInputSource'
import { DeploymentListActions } from './DeploymentListActions'
import { DeploymentPauseConfirm } from './DeploymentPauseConfirm'
import { DeploymentRestartConfirm } from './DeploymentRestartConfirm'
import { DeploymentStatusIndicator } from './DeploymentStatusIndicator/DeploymentStatusIndicator'

type SingleDeploymentGpuCellProps = {
  deployment: Deployment
  device?: Device
}

const SingleDeploymentGpuCell = ({
  deployment,
  device
}: SingleDeploymentGpuCellProps) => {
  if (!device || isJetsonDevice(device)) {
    return null
  }

  const gpu = resolveSingleDeploymentGpu(deployment, getDeviceGpus(device))

  if (!gpu) {
    return null
  }

  if (gpu.kind === 'device') {
    return (
      <Text size="xs" c="dimmed">
        <FormattedMessage id="deployments.list.single.gpuChosenByDevice" />
      </Text>
    )
  }

  return (
    <Text size="xs">
      <FormattedMessage
        id={gpu.name ? 'deployments.list.group.gpu' : 'devices.gpu.indexLabel'}
        values={{ index: gpu.index, name: gpu.name }}
      />
    </Text>
  )
}

type OwnContainerBadgeProps = {
  reasons: string[]
}

const OwnContainerBadge = ({ reasons }: OwnContainerBadgeProps) => {
  return (
    <Tooltip
      label={
        <Stack gap={4}>
          {reasons.map((reason) => {
            const { labelId, hintId } = getGroupExclusionReasonIds(reason)

            return (
              <Text key={reason} size="xs">
                <Text fw={600} span>
                  <FormattedMessage id={labelId} values={{ reason }} />
                </Text>
                {': '}
                <FormattedMessage id={hintId} values={{ reason }} />
              </Text>
            )
          })}
        </Stack>
      }
      maw={360}
      multiline
    >
      <Badge size="xs" variant="light" color="gray" mt={6}>
        <FormattedMessage id="deployments.list.single.ownContainer" />
      </Badge>
    </Tooltip>
  )
}

type DeploymentListItemProps = {
  deployment: Deployment
  device?: Device
  groupsEnabled?: boolean
}

export const DeploymentListItem = ({
  deployment,
  device,
  groupsEnabled = false
}: DeploymentListItemProps) => {
  const navigate = useNavigate()

  const [canUpdateDeployment, canDeleteDeployment] = useHasPermissions(
    Resource.Deployments,
    [CrudAction.Update, CrudAction.Delete]
  )

  const [
    isPauseConfirmOpen,
    { open: openPauseConfirm, close: closePauseConfirm }
  ] = useDisclosure(false)

  const [
    isRestartConfirmOpen,
    { open: openRestartConfirm, close: closeRestartConfirm }
  ] = useDisclosure(false)

  const [
    isDeleteConfirmOpen,
    { open: openDeleteConfirm, close: closeDeleteConfirm }
  ] = useDisclosure(false)

  const [
    isDetailsDrawerOpen,
    { open: openDetailsDrawer, close: closeDetailsDrawer }
  ] = useDisclosure(false)

  const isCameraDeployment =
    deployment.settings_payload?.input_source_type === InputSourceType.Cameras

  const camera = useMemo(() => {
    const { settings_payload } = deployment

    if (
      !device ||
      settings_payload?.input_source_type !== InputSourceType.Cameras
    ) {
      return null
    }

    return (
      getDevicesCameras(device).find(
        (c) => c.camera_id === settings_payload.stream_id
      ) || null
    )
  }, [device, deployment])

  const exclusionReasons = getSingleCameraReasons(deployment)

  const handleEditDeployment = useCallback(() => {
    void navigate(buildCreateDeploymentLink(deployment.application_id))
  }, [navigate, deployment.application_id])

  return (
    <>
      <Table.Tr style={{ cursor: 'pointer' }} onClick={openDetailsDrawer}>
        <Table.Td>
          <DeploymentInputSource
            camera={camera}
            deployment={deployment}
            deviceActive={device?.active ?? false}
          />

          {groupsEnabled &&
            isCameraDeployment &&
            exclusionReasons.length > 0 && (
              <OwnContainerBadge reasons={exclusionReasons} />
            )}
        </Table.Td>

        <Table.Td>
          <SingleDeploymentGpuCell deployment={deployment} device={device} />
        </Table.Td>

        <Table.Td>
          <DeploymentStatusIndicator
            status={deployment.status}
            statusUpdatedAt={deployment.status_updated_at}
            deviceActive={device?.active}
          />

          <DeploymentErrorSpoiler
            status={deployment.status}
            errorBody={deployment.error_body}
          />
        </Table.Td>

        <Table.Td onClick={(e) => e.stopPropagation()}>
          <DeploymentListActions
            deployment={deployment}
            canUpdateDeployment={canUpdateDeployment}
            canDeleteDeployment={canDeleteDeployment}
            onEditDeployment={handleEditDeployment}
            onPauseDeployment={openPauseConfirm}
            onResumeDeployment={openPauseConfirm}
            onRestartDeployment={openRestartConfirm}
            onDeleteDeployment={openDeleteConfirm}
          />
        </Table.Td>
      </Table.Tr>

      <DeploymentDetailsDrawer
        opened={isDetailsDrawerOpen}
        deployment={deployment}
        onClose={closeDetailsDrawer}
      />

      <DeploymentPauseConfirm
        opened={isPauseConfirmOpen}
        deploymentId={deployment.id}
        isPaused={deployment.status === DeploymentStatus.Stopped}
        onClose={closePauseConfirm}
      />

      <DeploymentRestartConfirm
        opened={isRestartConfirmOpen}
        deploymentId={deployment.id}
        onClose={closeRestartConfirm}
      />

      <DeploymentDeleteConfirm
        opened={isDeleteConfirmOpen}
        applicationId={deployment.application_id}
        applicationName={deployment.application_name}
        deploymentId={deployment.id}
        showOcrWarning={
          (deployment.ocr_settings?.whitelist.length ?? 0) > 0 ||
          (deployment.ocr_settings?.blacklist.length ?? 0) > 0
        }
        onClose={closeDeleteConfirm}
      />
    </>
  )
}
